import { CopilotDocument, CopilotRecipient, CopilotRecipients } from '../../../_interfaces/document';

export function emptyRecipients(): CopilotRecipients {
  return {
    primaryRecipient: null,
    recipientSalutation: null,
    ccRecipients: [],
  };
}

export function cloneRecipients(recipients?: CopilotRecipients | null): CopilotRecipients {
  return {
    primaryRecipient: recipients?.primaryRecipient ? { ...recipients.primaryRecipient } : null,
    recipientSalutation: recipients?.recipientSalutation ?? null,
    ccRecipients: (recipients?.ccRecipients ?? []).map((recipient: CopilotRecipient) => ({ ...recipient })),
  };
}

/** Nulls previous primary fields the next primary does not send, so a deep merge cannot keep them. */
export function nullAbsentRecipientFields(
  previous: CopilotRecipient | null | undefined,
  next: CopilotRecipient | null | undefined,
): CopilotRecipient | null | undefined {
  if (!previous || !next) return next;
  const cleared: CopilotRecipient = { ...next };
  for (const key of Object.keys(previous) as (keyof CopilotRecipient)[]) {
    if (previous[key] == null) continue;
    if (!Object.prototype.hasOwnProperty.call(next, key) || next[key] === undefined) {
      (cleared as Record<string, unknown>)[key] = null;
    }
  }
  return cleared;
}

/** Returns a clone with manual (free text) primary and CC entries removed, or null if none were present. */
export function withoutManualRecipients(recipients?: CopilotRecipients | null): CopilotRecipients | null {
  if (!recipients) return null;
  const hadManualPrimary = isManualRecipient(recipients.primaryRecipient);
  const manualCc = ccRecipients(recipients).filter(isManualRecipient);
  if (!hadManualPrimary && manualCc.length === 0) return null;

  const next = cloneRecipients(recipients);
  if (hadManualPrimary) {
    next.primaryRecipient = null;
  }
  next.ccRecipients = ccRecipients(next).filter((item) => !isManualRecipient(item));
  return next;
}

export function isRecipientManagementEnabled(
  doc?: CopilotDocument | null,
  options?: { enableDictate?: boolean; workflowTrigger?: boolean },
): boolean {
  return !!doc
    && doc.templateType === 'shared'
    && doc.recipients !== undefined
    && options?.enableDictate !== false
    && options?.workflowTrigger !== false;
}

export function isManualRecipient(recipient?: CopilotRecipient | null): boolean {
  return !!recipient?.content && !recipient.first_name && !recipient.last_name;
}

export function hasPrimaryRecipient(recipients?: CopilotRecipients | null): boolean {
  return isPresentRecipient(recipients?.primaryRecipient);
}

export function ccRecipients(recipients?: CopilotRecipients | null): CopilotRecipient[] {
  return (recipients?.ccRecipients ?? []).filter(isPresentRecipient);
}

export function isPresentRecipient(recipient?: CopilotRecipient | null): recipient is CopilotRecipient {
  if (!recipient) return false;
  return !!(
    recipient.content
    || recipient.first_name
    || recipient.last_name
    || recipient.recipient_id
    || recipient.site_name
  );
}

export function displayName(recipient?: CopilotRecipient | null): string {
  if (!recipient) return '';
  if (isManualRecipient(recipient)) {
    return manualName(recipient.content ?? '');
  }
  const last = recipient.last_name?.trim();
  const first = [recipient.prefix, recipient.first_name, recipient.middle_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || recipient.site_name || recipient.content || '';
}

export function titleAbbreviation(recipientType?: string | null): string | null {
  if (!recipientType?.trim()) return null;
  const words = recipientType
    .trim()
    .split(/\s+/)
    .filter((word) => word.toLowerCase() !== 'of');
  if (words.length === 0) return null;
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function formatAddressParts(recipient?: CopilotRecipient | null): string[] {
  if (!recipient) return [];
  if (isManualRecipient(recipient)) {
    const remainder = manualRemainder(recipient.content ?? '');
    return remainder ? [remainder] : [];
  }
  return [
    recipient.site_name,
    recipient.address_street,
    recipient.address_other,
    recipient.address_city,
    recipient.address_state,
    recipient.address_postcode,
    recipient.address_country,
  ].filter((part): part is string => !!part?.trim());
}

export function formatAddressLine(recipient?: CopilotRecipient | null): string {
  return formatAddressParts(recipient).join(', ');
}

export function formatAddressBullets(recipient?: CopilotRecipient | null): string {
  return formatAddressParts(recipient).join(' • ');
}

export function formatManualPreview(recipient?: CopilotRecipient | null): string {
  if (!recipient) return '';
  const name = displayName(recipient);
  const rest = formatAddressLine(recipient);
  return [name, rest].filter(Boolean).join(', ');
}

const ADDRESS_FIELDS = [
  'site_name',
  'address_street',
  'address_other',
  'address_city',
  'address_state',
  'address_postcode',
  'email',
] as const satisfies readonly (keyof CopilotRecipient)[];

function addressIdentity(recipient: CopilotRecipient): string {
  return ADDRESS_FIELDS.map((field) => recipient[field]).filter(Boolean).join('|');
}

export function recipientKey(recipient: CopilotRecipient): string {
  if (isManualRecipient(recipient)) return `manual:${recipient.content}`;
  const address = addressIdentity(recipient);
  if (recipient.recipient_id) {
    return address ? `id:${recipient.recipient_id}|${address}` : `id:${recipient.recipient_id}`;
  }
  return [recipient.first_name, recipient.last_name, address].filter(Boolean).join('|');
}

export function fromAddressBookRow(row: { id: number; address: CopilotRecipient }): CopilotRecipient {
  return {
    ...row.address,
    recipient_id: row.address?.recipient_id ?? String(row.id),
  };
}

export function matchesRecipientQuery(recipient: CopilotRecipient, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [
    displayName(recipient),
    recipient.recipient_type,
    formatAddressLine(recipient),
  ].some((value) => value?.toLowerCase().includes(needle));
}

function recipientSortLabel(recipient: CopilotRecipient): string {
  if (isManualRecipient(recipient)) return recipient.content ?? '';
  return displayName(recipient);
}

export function sortRecipientsByName(recipients: CopilotRecipient[]): CopilotRecipient[] {
  return [...recipients].sort((a, b) =>
    recipientSortLabel(a).toLowerCase().localeCompare(recipientSortLabel(b).toLowerCase()),
  );
}

export function generateRecipientSalutation(recipient?: CopilotRecipient | null): string | null {
  if (!recipient) return null;
  const prefix = recipient.prefix?.trim() ?? '';
  const lastName = recipient.last_name?.trim() ?? '';
  if (!prefix && !lastName) return null;
  return prefix ? `${prefix} ${lastName}` : lastName;
}

export function extraCcCount(recipients?: CopilotRecipients | null): number {
  return Math.max(0, ccRecipients(recipients).length - 1);
}

function manualName(content: string): string {
  const firstLine = content.split(/\n/)[0]?.trim() ?? '';
  const beforeComma = firstLine.split(',')[0]?.trim() ?? '';
  return beforeComma || firstLine;
}

function manualRemainder(content: string): string {
  const firstLine = content.split(/\n/)[0]?.trim() ?? '';
  const parts = firstLine.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length > 1) return parts.slice(1).join(', ');
  const rest = content.split(/\n/).slice(1).join(' ').trim();
  return rest;
}
